import React, { useMemo } from 'react';
import {
  containsHtml,
  parseJobDescriptionBlocks,
} from '../../utils/formatPlainJobDescription';
import { normalizeFeaturedRichHtml } from '../../utils/normalizeFeaturedRichHtml';

interface JobDescriptionProps {
  description?: string | null;
  renderAsHtml?: boolean;
}

const JobDescription: React.FC<JobDescriptionProps> = ({
  description,
  renderAsHtml = false,
}) => {
  const shouldRenderAsHtml = renderAsHtml || containsHtml(description);
  const blocks = useMemo(
    () => (shouldRenderAsHtml ? [] : parseJobDescriptionBlocks(description)),
    [description, shouldRenderAsHtml]
  );

  const richHtml = useMemo(
    () =>
      shouldRenderAsHtml && containsHtml(description)
        ? normalizeFeaturedRichHtml(description)
        : '',
    [description, shouldRenderAsHtml]
  );

  if (shouldRenderAsHtml) {
    const content = description?.trim() ?? '';
    if (!content) {
      return null;
    }

    if (richHtml) {
      return (
        <div
          className="job-description-rich"
          dangerouslySetInnerHTML={{ __html: richHtml }}
        />
      );
    }

    return <div className="job-description-plain">{content}</div>;
  }

  if (blocks.length === 0) {
    return null;
  }

  return (
    <div className="job-description">
      {blocks.map((block, index) => {
        if (block.type === 'section') {
          return (
            <h3 key={`section-${index}`} className="job-description-section">
              {block.text}
            </h3>
          );
        }

        if (block.type === 'list') {
          return (
            <ul key={`list-${index}`} className="job-description-list">
              {block.items.map((item, itemIndex) => (
                <li key={`${item}-${itemIndex}`}>{item}</li>
              ))}
            </ul>
          );
        }

        return (
          <p key={`paragraph-${index}`} className="job-description-paragraph">
            {block.text}
          </p>
        );
      })}
    </div>
  );
};

export default JobDescription;
